import React, { useMemo } from 'react';
import { AIConfigModalProps } from '../../types';

const AIConfigModal: React.FC<AIConfigModalProps> = ({
    isOpen,
    onClose,
    aiConfig,
    testwerkApiKey,
    setTestwerkApiKey,
    agentApiKey,
    setAgentApiKey,
    selectedModel,
    setSelectedModel,
    onUpdateConfig,
    aiMode = 'cloud',
    availableProviders = {}
}) => {
    // Provider detection logic
    const currentProvider = useMemo(() => {
        if (selectedModel.includes('gpt')) return 'OpenAI';
        if (selectedModel.includes('claude')) return 'Anthropic';
        if (selectedModel.includes('gemini')) return 'Google';
        if (selectedModel.includes('mistral')) return 'Mistral';
        return 'Unknown';
    }, [selectedModel]);

    const getApiKeyPlaceholder = () => {
        switch (currentProvider) {
            case 'OpenAI': return 'Enter your OpenAI API key (sk-...)';
            case 'Anthropic': return 'Enter your Anthropic/Claude API key';
            case 'Google': return 'Enter your Google/Gemini API key';
            case 'Mistral': return 'Enter your Mistral API key';
            default: return 'Enter your API key';
        }
    };

    const getApiKeyStatus = () => {
        // This should be enhanced to check provider-specific status
        return aiConfig?.testwerk_api_key_configured;
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content ai-config-modal" onClick={e => e.stopPropagation()}>
                <h2> AI Configuration</h2>
                {aiMode === 'cloud' ? (
                    <>
                        {/* CLOUD MODE - Show API keys and provider selection */}
                        <div className="config-section">
                            <h3>Cloud AI Provider</h3>
                            <select
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value as any)}
                                className="model-select"
                            >
                                {Object.entries(availableProviders).map(([providerId, provider]: [string, any]) => {
                                    if (provider.type === 'cloud') {
                                        return (
                                            <optgroup key={providerId} label={provider.display_name}>
                                                {provider.models?.map((model: any) => (
                                                    <option key={model.id} value={model.id}>
                                                        {model.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        );
                                    }
                                    return null;
                                })}
                            </select>

                            <h3>API Key for {currentProvider}</h3>
                            <p>Used by TestWerk.ai for test generation and validation</p>
                            <input
                                type="password"
                                placeholder={getApiKeyPlaceholder()}
                                value={testwerkApiKey}
                                onChange={(e) => setTestwerkApiKey(e.target.value)}
                                className="api-key-input"
                            />
                            <div className="key-status">
                                Status: {aiConfig?.testwerk_api_key_configured ?
                                    <span className="status-configured">✅ Configured</span> :
                                    <span className="status-not-configured">❌ Not Configured</span>}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {/* LOCAL MODE - Show local model selection */}
                        <div className="config-section">
                            <h3>Local AI Model (Ollama)</h3>
                            <select
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value as any)}
                                className="model-select"
                            >
                                {Object.entries(availableProviders).map(([providerId, provider]: [string, any]) => {
                                    if (provider.type === 'local') {
                                        return (
                                            <optgroup key={providerId} label={provider.display_name}>
                                                {provider.models?.map((model: any) => (
                                                    <option key={model.id} value={model.id}>
                                                        {model.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        );
                                    }
                                    return null;
                                })}
                            </select>

                            <div className="local-ai-status">
                                <h3>Local AI Status</h3>
                                <p>🖥️ Using Ollama at localhost:11434</p>
                                <p>✅ No API key required for local models</p>
                                <div className="info-box" style={{
                                    marginTop: '10px',
                                    padding: '10px',
                                    backgroundColor: '#f0f8ff',
                                    borderRadius: '5px',
                                    border: '1px solid #d0e8ff'
                                }}>
                                    <strong>To add more local models:</strong>
                                    <ol style={{ marginLeft: '20px', marginTop: '5px' }}>
                                        <li>Install: <code>ollama pull model-name</code></li>
                                        <li>Add to <code>config/ai_providers.json</code></li>
                                        <li>Restart the service</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Agent API Key section - shown for both modes */}
                <div className="config-section">
                    <h3>Agent Under Test API Key</h3>
                    <p>Used by your agent for live API calls (optional)</p>
                    <input
                        type="password"
                        placeholder="Enter API key for your agent"
                        value={agentApiKey}
                        onChange={(e) => setAgentApiKey(e.target.value)}
                        className="api-key-input"
                    />
                    <div className="key-status">
                        Status: {aiConfig?.agent_api_key_configured ?
                            <span className="status-configured">✅ Configured</span> :
                            <span className="status-not-configured">⚪ Optional</span>}
                    </div>
                </div>


                <div className="modal-actions">
                    <button className="pushable push-secondary" onClick={onClose}>
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Cancel</span>
                    </button>
                    <button className="pushable push-success" onClick={onUpdateConfig}>
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Save AI Configuration</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIConfigModal;